import type { FC } from 'react';

import { StyledEngineProvider, ThemeProvider } from '@mui/material';
import { ThemeProvider as StylesThemeProvider } from '@mui/styles';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createBrowserHistory } from 'history';
import { ModalProvider } from 'react-modal-hook';
import { Router } from 'react-router-dom';
import { TransitionGroup } from 'react-transition-group';

import { AuthProvider } from 'contexts/Auth0Context';
import { FiltersContextProvider } from 'contexts/FiltersContext';
import { SandboxContextProvider } from 'contexts/SandboxContext';

import { ImpersonationBar } from 'layouts/2Pane/ImpersonationBar';

import AnalyticsIntegration from 'components/AnalyticsIntegration';
import { DelightedSurvey } from 'components/DelightedSurvey';
import GlobalStyles from 'components/GlobalStyles';
import GoogleAnalytics from 'components/GoogleAnalytics';
import GoogleMaps from 'components/GoogleMaps';
import ScrollReset from 'components/ScrollReset';
import ZendeskChat from 'components/ZendeskChat';

import { ThemedSnackbarProvider } from 'theme/ThemedSnackbarProvider';

import { RoutesWrapper } from 'RoutesWrapper';
import { theme } from 'theme';

const history = createBrowserHistory();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchInterval: false,
      refetchIntervalInBackground: false,
      suspense: false,
      retryOnMount: false,
    },
  },
});

const App: FC = () => (
  <StyledEngineProvider injectFirst>
    <ThemeProvider theme={theme}>
      <StylesThemeProvider theme={theme}>
        <QueryClientProvider client={queryClient}>
          {!import.meta.env.PROD && <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />}
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <ThemedSnackbarProvider>
              <ModalProvider rootComponent={TransitionGroup}>
                <Router history={history}>
                  <AuthProvider>
                    <ImpersonationBar />
                    <SandboxContextProvider>
                      <FiltersContextProvider>
                        <GlobalStyles />
                        <ScrollReset />
                        <GoogleAnalytics />
                        <ZendeskChat />
                        <DelightedSurvey />
                        <AnalyticsIntegration history={history} />
                        <RoutesWrapper />
                      </FiltersContextProvider>
                    </SandboxContextProvider>
                  </AuthProvider>
                </Router>
              </ModalProvider>
              <GoogleMaps />
            </ThemedSnackbarProvider>
          </LocalizationProvider>
        </QueryClientProvider>
      </StylesThemeProvider>
    </ThemeProvider>
  </StyledEngineProvider>
);

export default App;
